import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import red from '@material-ui/core/colors/red';
import { Icon, Button } from '@material-ui/core';
import { Link } from 'react-router-dom';
import moment from 'moment';

import ServiceTicket from '../components/ServiceTicket';
import ContentHeader from '../components/ContentHeader';
import { getMinPrice } from '../helpers/ticket';
import globalStyles from '../styles';

import './Service.css';

const styles = theme => ({
    root: {
        background: '#fff',
        border: '0',
        maxWidth: '800px',
        margin: '40px auto',
        paddingBottom: '1px',
        position: 'relative',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    nameContainer: {
        padding: '10px 10px',
        background: '#70d0ff'
    },
    name: {
        color: '#fff'
    },
    description: {
        marginBottom: '20px',
        paddingRight: '20px'
    },
    serviceTickets: {
        margin: '0 20px 0px',
        padding: '0 0 20px'
    },
    error: {
        margin: '40px 20px',
        color: red[500]
    },
    spinner: {
        margin: '40px auto',
        display: 'block'
    },
    operatorInfo: {
        padding: '0 20px'
    },
    icon: {
        fontSize: '15px'
    },
    button: {
        paddingLeft: '2px',
        paddingRight: '4px'
    },
    link: globalStyles.boldLink
});

class Service extends Component {
    static formatDescription(description) {
        return description.split('*').filter(el => el !== '');
    }

    constructor(props) {
        super(props);

        this.state = {
            service: null,
            client: null,
            loading: true,
            error: false
        };
    }

    componentDidMount() {
        const {
            match: {
                params: { clientSlug, serviceId }
            }
        } = this.props;

        if (!clientSlug || !serviceId) {
            return;
        }

        this.getService(clientSlug, serviceId);
    }

    async getService(clientSlug, serviceId) {
        try {
            const client = await axios.get(
                `${
                    process.env.REACT_APP_API_ENDPOINT
                }/client/slug/${clientSlug}`
            );

            const { services = [] } = client.data;

            this.setState({
                client: client.data
            });

            if (services.length) {
                let serviceFound = false;
                for (let i = 0; i < services.length; i += 1) {
                    const service = services[i];
                    if (service.id === serviceId) {
                        serviceFound = true;
                        this.getTicketData(client.data, service, serviceId);
                    }
                }

                if (!serviceFound) {
                    throw new Error();
                }
            } else {
                throw new Error();
            }
        } catch (e) {
            this.setState({ error: true, loading: false });
        }
    }

    getTicketData(client, service, serviceId) {
        const salesYearTicketRq = [];
        for (let i = 0; i < client.sales_year.length; i += 1) {
            const salesYearTicketUrl = `${
                process.env.REACT_APP_TICKET_API_ENDPOINT
            }/service/${serviceId}/tickets/${client.sales_year[i].replace(
                '/',
                '-'
            )}`;
            salesYearTicketRq.push(axios.get(salesYearTicketUrl));
        }
        if (salesYearTicketRq.length === 0) {
            throw new Error('No services');
        }

        const now = moment().toISOString();
        axios
            .all(salesYearTicketRq)
            .then(
                axios.spread((...responses) => {
                    const tickets = [];
                    responses.forEach(res => {
                        if (res.data && res.data.length > 0) {
                            res.data.forEach(item => {
                                tickets.push(item);
                            });
                        }
                    });

                    const generalSaleTickets = tickets.filter(ticket => {
                        const isPrivateSale =
                            'is_private' in ticket && ticket.is_private;
                        const isDisabled =
                            'is_enabled' in ticket &&
                            ticket.is_enabled === false;
                        const isSimplePass =
                            'payment_type' in ticket &&
                            ticket.payment_type === 'ext';
                        const isExpiredFixedReturn =
                            ticket.type === 'return' &&
                            ticket.sub_type === 'fixed' &&
                            ticket.expiry < now;
                        return !(
                            isPrivateSale ||
                            isDisabled ||
                            isSimplePass ||
                            isExpiredFixedReturn
                        );
                    });

                    generalSaleTickets
                        .sort((a, b) => {
                            const aPrice = Number(getMinPrice(a, service));
                            const bPrice = Number(getMinPrice(b, service));

                            if (aPrice < bPrice) {
                                return -1;
                            }

                            if (aPrice > bPrice) {
                                return 1;
                            }

                            if (a.expiry > b.expiry) {
                                return -1;
                            }

                            if (a.expiry < b.expiry) {
                                return 1;
                            }

                            return 0;
                        })
                        .reverse();

                    this.setState({
                        service: { ...service, tickets: generalSaleTickets },
                        loading: false,
                        error: false
                    });
                })
            )
            .catch(e => {
                console.error(e);
                this.setState({ error: true, loading: false });
            });
    }

    filterTicketsByYear(year) {
        const { service } = this.state;
        return service.tickets.filter(ticket => {
            return ticket.year === year;
        });
    }

    render() {
        const { service, client, loading, error } = this.state;
        const {
            match: {
                params: { clientSlug, serviceId }
            },
            classes
        } = this.props;

        if (error) {
            return (
                <div>
                    <Typography
                        variant="h6"
                        align="center"
                        className={classes.error}
                    >
                        There was a problem retrieving the ticket info, please
                        try again later
                    </Typography>
                </div>
            );
        }

        if (loading) {
            return (
                <CircularProgress
                    id="loading-spinner"
                    className={classes.spinner}
                />
            );
        }

        const isVouchersVisible = client.features.buy_vouchers !== false;

        const ticketsByYear = {};

        client.sales_year.forEach(year => {
            if (
                !service.disable_year ||
                !(service.disable_year instanceof Array) ||
                !service.disable_year.includes(year)
            ) {
                ticketsByYear[year] = this.filterTicketsByYear(year);
            }
        });

        return (
            <div className={`${classes.root} service-ticket-listing`}>
                <ContentHeader text={service.name} id="service-name" />
                <div style={{ padding: '20px 20px 0' }}>
                    <Typography variant="body2">
                        Service operated by {client.name}.
                    </Typography>
                </div>
                <div id="service-description" className={classes.description}>
                    <ul>
                        {Service.formatDescription(service.description).map(
                            item => (
                                <li key={item}>
                                    <Typography variant="body2">
                                        {item}
                                    </Typography>
                                </li>
                            )
                        )}
                    </ul>
                </div>
                <div className={classes.operatorInfo}>
                    <p>
                        {service.route_url && (
                            <a
                                style={{
                                    textDecoration: 'none',
                                    marginRight: '5px'
                                }}
                                href={service.route_url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className={classes.button}
                                    size="small"
                                >
                                    <Icon className={classes.icon}>
                                        location_on
                                    </Icon>{' '}
                                    Route
                                </Button>
                            </a>
                        )}

                        {service.timetable_url && (
                            <a
                                style={{
                                    textDecoration: 'none',
                                    marginRight: '5px'
                                }}
                                href={service.timetable_url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className={classes.button}
                                    size="small"
                                >
                                    <Icon className={classes.icon}>
                                        access_time
                                    </Icon>{' '}
                                    timetable
                                </Button>
                            </a>
                        )}

                        {service.terms_url && (
                            <a
                                style={{ textDecoration: 'none' }}
                                href={service.terms_url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className={classes.button}
                                    size="small"
                                >
                                    <Icon className={classes.icon}>list</Icon>
                                    Terms
                                </Button>
                            </a>
                        )}
                    </p>
                </div>
                {Object.keys(ticketsByYear).length === 0 && (
                    <>
                        <Typography
                            variant="h6"
                            style={{ padding: '0 20px 0px' }}
                        >
                            Tickets
                        </Typography>
                        <div
                            id="service-ticket-list"
                            className={classes.serviceTickets}
                            style={{
                                borderTop: '1px solid rgba(0, 0, 0, 0.26)'
                            }}
                        >
                            <p>
                                There are no tickets currently listed by{' '}
                                {client.name}.
                            </p>
                            <p>
                                Please contact {client.name} directly for more
                                information and ticket availability.
                            </p>
                        </div>
                    </>
                )}
                {Object.keys(ticketsByYear).map((ticketYear, i) => (
                    <Fragment key={ticketYear}>
                        {ticketsByYear[ticketYear].length > 0 && (
                            <>
                                <Typography
                                    variant="h6"
                                    style={{ padding: '0 20px 0px' }}
                                >
                                    {ticketYear} Tickets
                                </Typography>
                                <div
                                    id={`service-ticket-list-${i}`}
                                    className={classes.serviceTickets}
                                >
                                    {ticketsByYear[ticketYear].map(ticket => (
                                        <ServiceTicket
                                            key={ticket.id}
                                            clientSlug={clientSlug}
                                            ticket={ticket}
                                            serviceId={serviceId}
                                            service={service}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                        {ticketsByYear[ticketYear].length === 0 && (
                            <>
                                <Typography
                                    variant="h6"
                                    style={{ padding: '0 20px 0px' }}
                                >
                                    {ticketYear} Tickets
                                </Typography>
                                <div
                                    id={`service-ticket-list-${i}`}
                                    className={classes.serviceTickets}
                                    style={{
                                        borderTop:
                                            '1px solid rgba(0, 0, 0, 0.26)'
                                    }}
                                >
                                    <p>
                                        There are no tickets currently listed by{' '}
                                        {client.name}.
                                    </p>
                                    <p>
                                        Please contact {client.name} for ticket
                                        availability or join their waiting list.
                                    </p>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        component={Link}
                                        to={`/waiting-list/${ticketYear.replace(
                                            '/',
                                            '-'
                                        )}/${clientSlug}`}
                                    >
                                        Join waiting list
                                    </Button>
                                </div>
                            </>
                        )}
                    </Fragment>
                ))}
                {isVouchersVisible && (
                    <div className="voucher-promo">
                        <Typography
                            variant="h6"
                            style={{ fontSize: '16px', padding: '0px' }}
                        >
                            Buy vouchers
                        </Typography>
                        <div>
                            <Typography variant="body2">
                                Want to split the cost between parents or
                                family? Looking to gift the full value of a
                                ticket?{' '}
                                <a
                                    className={classes.link}
                                    href={`https://voucher.shuttleid.uk/client/${clientSlug}`}
                                >
                                    Buy a voucher
                                </a>{' '}
                                from the voucher checkout.
                            </Typography>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

Service.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            clientSlug: PropTypes.string,
            serviceId: PropTypes.string
        })
    }).isRequired,
    classes: PropTypes.shape().isRequired
};

export default withStyles(styles)(Service);
